/**
 * Session DTOs (`docs/design/00-contracts.md` §13.1, §13.3; C-23, C-30, C-33).
 *
 * Supabase Auth owns credentials; NestJS owns everything after that. `POST /v1/auth/session` is
 * the first call a client makes once Supabase returns a token: it provisions the person, records
 * any consent, and reports what is still outstanding — all in one transaction, so a failure
 * leaves no half-created person and no orphan consent row. It is idempotent.
 */
import { z } from 'zod';

import { ORGANIZATION_CONSENT_KINDS, USER_CONSENT_KINDS } from '../constants.js';
import {
  organizationKindSchema,
  orgRoleSchema,
  termsKindSchema,
  userStatusSchema,
} from '../enums.js';
import { localeSchema } from '../locale.js';
import { organizationModuleSchema } from './control-room-modules.js';

/** Canonical identity, the same in every organization. Org-specific data lives on `contacts`. */
export const personSummarySchema = z
  .object({
    id: z.uuid(),
    displayName: z.string(),
    givenName: z.string().nullable(),
    familyName: z.string().nullable(),
    avatarUrl: z.string().nullable(),
  })
  .meta({ id: 'PersonSummary' });

/**
 * The login identity. `email` is a display mirror of the Supabase subject, never an identity:
 * `sub` is the identity (§13.1). Locale and timezone live on `users` (C-1) — there is no
 * `user_settings` table.
 */
export const userAccountSchema = z
  .object({
    id: z.uuid(),
    email: z.email(),
    locale: localeSchema,
    timezone: z.string(),
    status: userStatusSchema,
  })
  .meta({ id: 'UserAccount' });

/**
 * Which `terms_versions` row the client displayed. The client sends the row's identity, never a
 * loose version string and never a timestamp: the server supplies the time, because a client
 * clock is not evidence (C-30).
 */
export const acceptedDocumentSchema = z
  .object({
    kind: z.enum(USER_CONSENT_KINDS),
    termsVersionId: z.uuid(),
  })
  .meta({ id: 'AcceptedDocument' });

export const createSessionRequestSchema = z
  .object({
    /** Sent on first sign-in. */
    acceptedDocuments: z.array(acceptedDocumentSchema).max(2).optional(),
    /** Locale detected by the client on first session. Ignored if the user row already exists. */
    initialLocale: localeSchema.optional(),
  })
  .meta({ id: 'CreateSessionRequest' });

/** An outstanding document. Non-empty `pendingConsents` blocks every org-scoped endpoint. */
export const pendingConsentSchema = z
  .object({
    kind: termsKindSchema,
    termsVersionId: z.uuid(),
    version: z.string(),
    title: z.string(),
    documentUrl: z.string(),
    locale: localeSchema,
    /**
     * Har mennesket godtatt en TIDLIGERE utgave av dette dokumentet?
     *
     * Velger setningen paa consent-sida, ikke oppfoerselen. En helt ny bruker fikk «Vi har
     * oppdaterte dokumenter du maa godta foer du kan fortsette» — en beskjed om at noe var endret
     * siden sist, til en som ikke hadde hatt en sist (QA15, steg 4).
     */
    seenBefore: z.boolean(),
  })
  .meta({ id: 'PendingConsent' });

export const sessionMembershipSchema = z
  .object({
    organizationId: z.uuid(),
    organizationName: z.string(),
    organizationSlug: z.string(),
    logoUrl: z.string().nullable(),
    role: orgRoleSchema,
    /**
     * Hva slags hus dette er (migrering 0081): `festival` eller `agency`.
     *
     * DEN LIGGER PÅ MEDLEMSKAPET og ikke bare på organisasjonssiden, fordi det er MENYEN som leser
     * den: skallet må vite om det skal tegne Festivaler og Booking eller Artister i samme øyeblikk
     * man bytter organisasjon, uten et kall til. Vedtak 93.
     */
    organizationKind: organizationKindSchema,
    /**
     * Modulene som er på (0483), i fast rekkefølge. Menyen, rutene og Rettigheter følger dem. API-et
     * sender alltid feltet; valgfritt bare så en eldre cache eller en testfikstur ikke feiler.
     */
    modules: z.array(organizationModuleSchema).optional(),
    /**
     * FJERNHJELP (A11, 1031): satt når medlemskapet er et supportmedlemskap fra Control Room, med når
     * det utløper. Skallet tegner det oransje båndet ut fra den. Utelatt eller null: et vanlig medlemskap.
     */
    supportUntil: z.iso.datetime().nullable().optional(),
  })
  .meta({ id: 'SessionMembership' });

export const sessionResponseSchema = z
  .object({
    person: personSummarySchema,
    user: userAccountSchema,
    memberships: z.array(sessionMembershipSchema),
    /** The organization the client should activate, or null when the person belongs to none yet. */
    activeOrganizationId: z.uuid().nullable(),
    /** Non-empty means the client MUST present the consent screen before proceeding. */
    pendingConsents: z.array(pendingConsentSchema),
    /** True when this call created the person. Lets the client route to onboarding without guessing. */
    isNewPerson: z.boolean(),
  })
  .meta({ id: 'SessionResponse' });

/**
 * The two documents the founding superadmin accepts on the organization's behalf. Both are
 * required by `POST /v1/organizations`; this schema exists here so the consent shape is defined
 * once for both flows.
 */
export const organizationAcceptedDocumentSchema = z
  .object({
    kind: z.enum(ORGANIZATION_CONSENT_KINDS),
    termsVersionId: z.uuid(),
  })
  .meta({ id: 'OrganizationAcceptedDocument' });

export type PersonSummary = z.infer<typeof personSummarySchema>;
export type UserAccount = z.infer<typeof userAccountSchema>;
export type AcceptedDocument = z.infer<typeof acceptedDocumentSchema>;
export type CreateSessionRequest = z.infer<typeof createSessionRequestSchema>;
export type PendingConsent = z.infer<typeof pendingConsentSchema>;
export type SessionMembership = z.infer<typeof sessionMembershipSchema>;
export type SessionResponse = z.infer<typeof sessionResponseSchema>;
export type OrganizationAcceptedDocument = z.infer<typeof organizationAcceptedDocumentSchema>;
